/** RSVP buttons, RSVP chip, check-in control and the hook that submits them with toasts. */
import { useQueryClient } from '@tanstack/react-query';
import { Check, CircleCheckBig, CircleHelp, Clock, CloudOff, Hand, X, type LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import type { EventDTO, Rsvp } from '../../../shared/dto/events';
import { onQueuedRejected, submitCheckin, submitRsvp, useParticipationQueued, type QueuedKind } from '../../api/calendar';
import { useToast } from '../../components/toast';
import { Button, cx } from '../../components/ui';
import { useI18n, useT, type MessageKey } from '../../i18n';
import { checkinWindow, formatTime, isCheckinOpen, todayStr } from '../../lib/dates';
import { errorText, OK_FILL, RSVP_KEY, WARN_FILL } from './shared';

/** Submits RSVP / check-in (optimistic) and reports offline queueing or errors with toasts. */
export function useParticipation(ev: EventDTO) {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();

  /** `note` only when the user edited it (null clears); omitted → the server keeps the current note. */
  const setRsvp = useCallback(
    async (rsvp: Rsvp, note?: string | null) => {
      try {
        const r = await submitRsvp(ev.id, note === undefined ? { rsvp } : { rsvp, note }, qc);
        if (r === 'queued') toast.show(t('calendar.saved_offline'));
        return true;
      } catch (e) {
        toast.show(errorText(t, e), { tone: 'error' });
        return false;
      }
    },
    [ev.id, qc, toast, t],
  );

  const checkin = useCallback(async () => {
    try {
      const r = await submitCheckin(ev.id, qc);
      toast.show(r === 'queued' ? t('calendar.saved_offline') : t('calendar.checkin.saved'), { tone: r === 'queued' ? 'info' : 'ok' });
    } catch (e) {
      toast.show(errorText(t, e), { tone: 'error' });
    }
  }, [ev.id, qc, toast, t]);

  return { setRsvp, checkin };
}

const REJECTED_KEY: Record<QueuedKind, MessageKey> = {
  rsvp: 'calendar.rejected.rsvp',
  checkin: 'calendar.rejected.checkin',
  comment: 'calendar.rejected.comment',
  roster: 'calendar.rejected.roster',
};

/**
 * Shows a sticky "Ekki vistað" toast when something saved offline (RSVP, check-in, comment, roster
 * mark) was rejected by the server once the network came back. Mount on every calendar screen;
 * only the latest mounted one reports, and rejections that happened elsewhere show on the next mount.
 */
export function useQueuedRejectionToasts(): void {
  const toast = useToast();
  const t = useT();
  const ref = useRef({ toast, t });
  useEffect(() => {
    ref.current = { toast, t };
  });
  useEffect(
    () =>
      onQueuedRejected(({ kind, error }) => {
        const { toast: tt, t: tr } = ref.current;
        tt.show(`${tr(REJECTED_KEY[kind])} ${errorText(tr, error)}`, { tone: 'error', sticky: true });
      }),
    [],
  );
}

const OPTIONS: { value: Rsvp; icon: LucideIcon; on: string }[] = [
  { value: 'yes', icon: Check, on: `${OK_FILL} text-white border-transparent` },
  { value: 'no', icon: X, on: 'bg-[var(--ink-2)] text-[var(--surface)] border-transparent' },
  { value: 'maybe', icon: CircleHelp, on: `${WARN_FILL} text-white border-transparent` },
];

/**
 * Three big buttons: Kem / Kem ekki / Óvíst, showing the effective state. Until the member answers
 * none is pressed and an accent line asks for an answer (the cards around show an "Ósvarað" chip).
 */
export function RsvpButtons({
  ev,
  onPick,
  disabled,
  size = 'lg',
  showHint = true,
}: {
  ev: EventDTO;
  onPick: (r: Rsvp) => void;
  disabled?: boolean;
  size?: 'md' | 'lg';
  showHint?: boolean;
}) {
  const t = useT();
  const queued = useParticipationQueued(ev.id);
  const current = ev.my.effectiveRsvp;
  return (
    <div className="@container flex flex-col gap-2">
      <div className="grid grid-cols-3 gap-2" role="group" aria-label={t('calendar.rsvp.question')}>
        {OPTIONS.map((o) => {
          const on = current === o.value;
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() => !on && onPick(o.value)}
              className={cx(
                'flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-2xl border-2 px-1.5 py-1 font-semibold transition select-none disabled:cursor-not-allowed disabled:opacity-50 @sm:flex-row @sm:gap-1.5',
                size === 'lg' ? 'min-h-16 text-[17px] @sm:min-h-14 @sm:text-[18px]' : 'min-h-14 text-[16px] @sm:min-h-12',
                on ? o.on : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink)] active:bg-[var(--accent-soft)]',
              )}
            >
              <o.icon className={cx('shrink-0', size === 'lg' ? 'size-6' : 'size-5')} aria-hidden strokeWidth={on ? 3 : 2} />
              <span className="max-w-full truncate">{t(RSVP_KEY[o.value])}</span>
            </button>
          );
        })}
      </div>
      {showHint && current === null && !disabled ? (
        <p className="text-[15px] font-semibold text-[var(--accent)]">{t('calendar.rsvp.unanswered_hint')}</p>
      ) : null}
      {queued ? <PendingNote /> : null}
    </div>
  );
}

export function PendingNote() {
  const t = useT();
  return (
    <p className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[var(--ink-2)]">
      <CloudOff className="size-4 text-[var(--warn-text)]" aria-hidden />
      {t('calendar.pending_send')}
    </p>
  );
}

/** Compact read-only chip of my RSVP (agenda rows). */
export function RsvpChip({ ev, className }: { ev: EventDTO; className?: string }) {
  const t = useT();
  const r = ev.my.effectiveRsvp;
  // Text stays in --ink / --muted for contrast in both themes; colour is carried by tint + icon.
  const style =
    r === 'yes'
      ? 'bg-[color-mix(in_srgb,var(--ok)_16%,transparent)] text-[var(--ink)]'
      : r === 'no'
        ? 'bg-[var(--surface-2)] text-[var(--muted)]'
        : r === 'maybe'
          ? 'bg-[color-mix(in_srgb,var(--warn)_18%,transparent)] text-[var(--ink)]'
          : 'border border-[var(--accent)] text-[var(--accent)]';
  const iconColor = r === 'yes' ? 'text-[var(--ok-text)]' : r === 'maybe' ? 'text-[var(--warn-text)]' : '';
  const Icon = r === 'yes' ? Check : r === 'no' ? X : r === 'maybe' ? CircleHelp : Clock;
  return (
    <span className={cx('inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[14px] font-semibold', style, className)}>
      <Icon className={cx('size-4', iconColor)} aria-hidden strokeWidth={2.5} />
      {r ? t(RSVP_KEY[r]) : t('calendar.rsvp.none')}
    </span>
  );
}

/**
 * Check-in: big button while the window is open (start − checkinOpensMin … end, server time),
 * "Mætt/ur kl. 19:28" once checked in, a hint before it opens on the same day.
 */
export function CheckinControl({ ev, now, onCheckin }: { ev: EventDTO; now: number; onCheckin: () => void }) {
  const t = useT();
  const { loc } = useI18n();
  const queued = useParticipationQueued(ev.id);
  if (ev.my.checkedInAt != null) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex min-h-14 items-center gap-3 rounded-2xl bg-[color-mix(in_srgb,var(--ok)_16%,transparent)] px-4 font-semibold text-[var(--ink)]">
          <CircleCheckBig className="size-7 shrink-0 text-[var(--ok-text)]" aria-hidden />
          <span className="text-[18px]">{t('calendar.checkin.done', { time: formatTime(ev.my.checkedInAt, loc) })}</span>
        </div>
        {queued ? <PendingNote /> : null}
      </div>
    );
  }
  if (ev.status === 'cancelled') return null;
  if (isCheckinOpen(ev, now)) {
    return (
      <Button size="lg" block icon={Hand} onClick={onCheckin} className="min-h-16 text-[20px]">
        {t('calendar.checkin.button')}
      </Button>
    );
  }
  const { opensAt } = checkinWindow(ev);
  if (now < opensAt && todayStr(opensAt) === todayStr(now)) {
    return (
      <p className="flex items-center gap-2 text-[15px] text-[var(--muted)]">
        <Clock className="size-4" aria-hidden />
        {t('calendar.checkin.opens_at', { time: formatTime(opensAt, loc) })}
      </p>
    );
  }
  return null;
}
